<template>
  <div class="cadastrar-noticia">
    <breadcrumb :items="breadcrumbItems" />

    <h2 class="page-title">{{isEdicao ? 'Editar' : 'Cadastrar'}} Notícia:</h2>

    <form class="form-layout">
      <div class="col-left">
        <FormField
          label="Título"
          id="titulo"
          required
          help-text="Informe o título da notícia"
        >
          <input
            id="titulo"
            v-model="form.titulo"
            type="text"
            class="field-input"
            required
          />
        </FormField>

        <FormField
          label="Fonte"
          id="fonte"
          required
          help-text="Informe a fonte da notícia"
        >
          <input
            id="fonte"
            v-model="form.fonte"
            type="text"
            class="field-input"
            required
          />
        </FormField>

        <imagemInput
          :imagem="form.imagem"
          @enviar-imagem="processarImagem"
          v-model="form.descricaoImagem"
        />

        <PalavrasChaveInput
          v-model="form.palavrasChaves"
          :error-message="
            mostrarErro && form.palavrasChaves.length === 0
              ? 'Adicione pelo menos uma palavra-chave'
              : ''
          "
        />

        <FormField
          label="Abrangência"
          required
          :help-text="textoAjudaAbrangencia"
          v-if="!isEdicao"
        >
          <div class="radio-group">
            <label
              v-for="abrangencia in abrangencias"
              :key="abrangencia.id"
              class="radio-label"
            >
              <input
                v-model="form.abrangenciaId"
                type="radio"
                :value="abrangencia.id"
                required
              />
              <span class="radio-custom"></span>
              {{ abrangencia.abrangencia.toUpperCase() === "ACAO" ? "AÇÃO" : abrangencia.abrangencia.toUpperCase() }}
            </label>
          </div>
        </FormField>
        <div class="publish-section">
          <FormField
            label="Tipo de publicação"
            required
            help-text="Escolha se a publicação será imediata ou programada para uma data futura"
            v-if="!isEdicao"
          >
            <div class="radio-group">
              <label class="radio-label">
                <input
                  v-model="form.publicacaoImediata"
                  type="radio"
                  :value="true"
                />
                <span class="radio-custom"></span>
                IMEDIATA
              </label>
              <label class="radio-label">
                <input
                  v-model="form.publicacaoImediata"
                  type="radio"
                  :value="false"
                />
                <span class="radio-custom"></span>
                PROGRAMADA
              </label>
            </div>
          </FormField>

          <div
            v-if="!form.publicacaoImediata"
            class="programada-container"
          >
            <FormField
              label="Data de publicação"
              id="dataPublicacao"
              required
              :help-text="
                isUnidadeOuPrex
                  ? 'Para Unidade ou PREX, a data deve ter no mínimo 7 dias de antecedência para avaliação'
                  : 'Selecione a data para publicação da notícia'
              "
              group-class="date-group"
              v-if="!isEdicao"
            >
              <div class="date-input-wrapper">
                <input
                  id="dataPublicacao"
                  v-model="form.dataPublicacao"
                  type="date"
                  class="field-input date-input"
                  placeholder="DD/MM/AAAA"
                  :min="dataMinimaPublicacao"
                  :max="dataMaximaPublicacao || undefined"
                />
              </div>
            </FormField>

            <p
              v-if="isUnidadeOuPrex"
              class="aviso-prazo-unidade"
            >
              <span class="aviso-icon">ℹ</span>
              Notícias com abrangência UNIDADE ou PREX exigem no mínimo 7 dias
              de antecedência em relação à data atual para avaliação do
              coordenador.
            </p>
          </div>
        </div>
      </div>

      <div class="col-right">
        <EditorTextoInput
          v-model="form.texto"
          label="Texto"
          id="texto"
          required
          help-text="Informe o texto da notícia"
          group-class="full-height"
          :rows="15"
          :error-message="
            mostrarErro && !temTextoValido
              ? 'O texto da notícia é obrigatório'
              : ''
          "
        />
        <div class="publish-row">
          <button
            class="btn-publicar"
            type="submit"
            :disabled="isCadastrando || isLoading"
            @click.prevent="publicar"
          >
            {{
              isCadastrando
                ? !form.publicacaoImediata
                  ? "Agendando..."
                  : "Publicando..."
                : textoBotaoPublicar
            }}
          </button>
        </div>

        <span
          v-if="erroData"
          class="mensagem-erro"
        >
          {{ erroData }}
        </span>
      </div>
    </form>

    <!-- Modal de Aviso -->
    <Aviso
      v-if="modal.visivel"
      :texto="modal.mensagem"
      :sucesso="modal.sucesso"
      @fecharModal="fecharModal"
    />
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Breadcrumb from "@/components/common/Breadcrumb.vue";
import FormField from "@/components/common/FormField.vue";
import imagemInput from "@/components/common/imagemInput.vue";
import PalavrasChaveInput from "@/components/common/PalavrasChaveInput.vue";
import EditorTextoInput from "@/components/common/EditorTextoInput.vue";
import Aviso from "@/components/common/aviso.vue";
import { useCadastroNoticia } from "@/composables/useCadastroNoticia";
import { useNoticias } from "@/composables/useNoticias";
import { useAtividades } from "@/composables/useAtividades";
import { carregarImagemUrlComoBase64 } from "@/utils/FormatarEntradas";
import {
  clearAtividadeEmCadastro,
  clearCadastroAtividadeTemp,
} from "@/utils/atividadeCadastroStorage";

const router = useRouter();
const route = useRoute();

const props = defineProps({
  acaoId: {
    type: String,
    default: null,
  },
  atividadeObjeto: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["fechar-form-noticia"]);

const textoAjudaAbrangencia = [
  "A abrangência AÇÃO publica uma notícia na área da ação (não requer aprovação);",
  "A abrangência UNIDADE publica uma notícia na área da unidade (requer aprovação);",
  "A abrangência PREX publica uma notícia na página principal do GEX (requer aprovação).",
].join("\n");

const { saveNoticiaAcao, isSaving: isSavingNoticia, fetchNoticiaById, updateNoticia, isLoading } = useNoticias();
const { saveAtividade, isSaving: isSavingAtividade } = useAtividades();

const isCadastrando = computed( () => isSavingNoticia.value || isSavingAtividade.value)

const isEdicao = computed(() => route.name === 'editar-noticia')

const textoBotaoPublicar = computed(() => {
  if(isEdicao.value){
    return "Editar"
  } else {
    return !form.value.publicacaoImediata ? "Agendar" : "Publicar";
  }
});

const breadcrumbItems = computed(() => {
  const acaoId = route.params.id || props.acaoId;

  let items = [
    { label: "Início", to: "/" },
    { label: "Ações", to: "/suas-acoes" },
    { tipo: "acao", id: acaoId, to: `/acao/${acaoId}` },
    { label: "Notícias", to: `/acao/${acaoId}/noticias` },
  ];

  if (isEdicao.value) {
    items = [
      ...items,
      {
        label: "Editar Notícia",
        to: `/acao/${acaoId}/editar-noticia/${route.params.noticiaId}`,
      },
    ];
  } else if (props.atividadeObjeto) {
    items = [
      ...items,
      {
        label: "Cadastrar Atividade",
        to: `/acao/${acaoId}/cadastrar-atividade`,
      },
    ];
  } else {
    items = [
      ...items,
      {
        label: "Cadastrar Notícia",
        to: `/acao/${acaoId}/cadastrar-noticia`,
      },
    ];
  }

  return items;
});

const {
  form,
  atividade: atividadeEmCadastro,
  modal,
  abrangencias,
  isUnidadeOuPrex,
  dataMinimaPublicacao,
  dataMaximaPublicacao,
  mostrarErro,
  erroData,
  temTextoValido,
  processarImagem,
  validarFormulario,
  definirAcaoAoFecharModal,
  abrirModal,
  abrirAvisoErro,
  buscarAbrangecias,
  fecharModal,
} = useCadastroNoticia({ props, emit });

async function publicar() {
  if (isCadastrando.value) return;

  const validacao = validarFormulario();
  if (!validacao.valido) return;

  try {
    if (validacao.isAtividade) {
      const resposta = await saveAtividade(validacao.payloadAtividade);

      clearAtividadeEmCadastro();
      clearCadastroAtividadeTemp();

      const finalAtividadeId = resposta?.id || resposta?.atividadeId;
      const finalNoticiaId =
        resposta?.noticia?.id ||
        resposta?.noticiaId ||
        resposta?.noticiaCriada?.id;

      definirAcaoAoFecharModal(() => {
        if (validacao.targetAcaoId && finalNoticiaId) {
          router.push(
            `/acao/${validacao.targetAcaoId}/noticias/${finalNoticiaId}`
          );
        } else if (validacao.targetAcaoId && finalAtividadeId) {
          router.push(
            `/acao/${validacao.targetAcaoId}/atividades/${finalAtividadeId}`
          );
        } else {
          router.push(`/acao/${validacao.targetAcaoId}/atividades`);
        }
        emit("fechar-form-noticia");
      });

      abrirModal(validacao.tipoModal, validacao.msgSucesso);
    } else if (!isEdicao.value){
      const resposta = await saveNoticiaAcao(
        validacao.payloadNoticia,
        validacao.acaoId
      );

      const finalNoticiaId =
        resposta?.id || resposta?.noticiaId || resposta?.data?.id;

      definirAcaoAoFecharModal(() => {
        if (validacao.acaoId && finalNoticiaId) {
          router.push(`/acao/${validacao.acaoId}/noticias/${finalNoticiaId}`);
        } else if (finalNoticiaId) {
          router.push(`/noticias/${finalNoticiaId}`);
        } else {
          router.push(`/acao/${validacao.acaoId}/noticias`);
        }
        emit("fechar-form-noticia");
      });

      abrirModal(validacao.tipoModal);
    } else {
      const resposta = await updateNoticia(
        validacao.payloadNoticia,
        validacao.payloadNoticia.id
      );

      const finalNoticiaId = resposta?.id;

      definirAcaoAoFecharModal(() => {
        if (validacao.acaoId && finalNoticiaId) {
          router.push(`/acao/${validacao.acaoId}/noticias/${finalNoticiaId}`);
        } else if (finalNoticiaId) {
          router.push(`/noticias/${finalNoticiaId}`);
        }
        emit("fechar-form-noticia");
      });

      abrirModal(validacao.tipoModal);
    }
  } catch (error) {
    console.error("Erro detalhado ao enviar notícia/atividade:", error);

    let mensagemErro = validacao.isAtividade
      ? "Erro ao cadastrar atividade e notícia."
      : "Erro ao enviar notícia.";

    if (error.response) {
      const data = error.response.data;
      if (data && data.message) {
        mensagemErro += `\nMotivo: ${data.message}`;
      } else if (typeof data === "string" && data.length < 100) {
        mensagemErro += `\nMotivo: ${data}`;
      } else {
        mensagemErro += `\nStatus: ${error.response.status} - ${error.response.statusText}`;
      }
    } else if (error.request) {
      mensagemErro += "\nO servidor não respondeu. Verifique sua conexão.";
    } else {
      mensagemErro += `\nErro: ${error.message}`;
    }

    abrirAvisoErro(mensagemErro);
  }
}

function processarAbrangencia() {
  form.value.abrangenciaId = abrangencias.value.find((abrangencia) => abrangencia.abrangencia === form.value.abrangenciaNome)?.id;
}

onMounted(async() => {
  await buscarAbrangecias()
  if(isEdicao.value){
    const noticiaId = route.params.noticiaId;
    form.value = await fetchNoticiaById(noticiaId)
    processarAbrangencia()
    form.value.imagem = await carregarImagemUrlComoBase64(form.value.imagemUrl)
  }
})
</script>

<style scoped>
.publish-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: auto;
  width: 100%;
}

.programada-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aviso-prazo-unidade {
  font-size: 0.8rem;
  color: var(--roxo-medio);
  background-color: var(--roxo-superficie);
  border: 1px solid var(--roxo-claro);
  border-radius: 4px;
  padding: 8px 10px;
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
}

.aviso-icon {
  font-weight: bold;
}

.mensagem-erro {
  color: var(--vermelho);
  font-size: 0.85rem;
  font-weight: 500;
  text-align: justify;
  width: 100%;
  display: block;
  word-wrap: break-word;
}

.header-nav {
  background: var(--cinza-claro);
  border-bottom: 1px solid var(--roxo-superficie);
  margin: -24px -32px 24px -32px;
  padding: 0 48px;
  width: 100vw;
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  box-sizing: border-box;
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 14px 0;
  font-size: 0.88rem;
  font-weight: 400;
  color: var(--cinza-escuro);
  text-decoration: none;
  border-bottom: 3px solid transparent;
  transition:
    color 0.2s,
    border-color 0.2s;
  white-space: nowrap;
}

.nav-item:hover {
  color: var(--roxo-medio);
  border-bottom-color: var(--roxo-claro);
}

.nav-item--active {
  color: var(--roxo-medio);
  border-bottom-color: var(--roxo-medio);
  font-weight: 600;
}

.cadastrar-noticia {
  font-family: var(--font-base);
  color: var(--preto);
}

.page-title {
  margin-bottom: 24px;
}

.form-layout {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.col-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.col-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Field groups */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-group.full-height {
  flex: 1;
}

.field-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--preto);
  display: flex;
  align-items: center;
  gap: 6px;
}

.field-label.required::before {
  content: "*";
  color: var(--roxo-medio);
}

.help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--cinza-medio);
  color: var(--branco);
  font-size: 0.65rem;
  font-weight: bold;
  cursor: pointer;
}

.field-input {
  border: 1px solid var(--roxo-claro);
  border-radius: 4px;
  padding: 8px 10px;
  font-size: 0.9rem;
  color: var(--preto);
  outline: none;
  transition: border-color 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.field-input:focus {
  border-color: var(--roxo-medio);
  box-shadow: 0 0 0 2px hsla(258, 46%, 46%, 0.12);
}

/* Radio */
.radio-group {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  border: 1px solid var(--roxo-claro);
  border-radius: 4px;
  padding: 10px 14px;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--preto);
  cursor: pointer;
  position: relative;
}

.radio-label input[type="radio"] {
  accent-color: var(--roxo-medio);
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.radio-custom {
  display: none;
}

/* Publish row */
.publish-row {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 16px;
}

.date-group {
  flex-direction: column;
  gap: 4px;
}

.date-input-wrapper {
  position: relative;
}

.date-input {
  padding-right: 36px;
  min-width: 160px;
}

/* .btn-publicar (base e :hover) removidos daqui: eram cópia
   exata do que já existe em _utilities.css. O :disabled abaixo
   é mantido pois é um estado que só existe neste componente. */
.btn-publicar:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .form-layout {
    flex-direction: column;
  }

  .col-left {
    flex: unset;
    width: 100%;
  }

  .publish-row {
    flex-direction: column;
    align-items: flex-end;
  }
}
</style>
